import { useEffect, useMemo, useState, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { apiGet, apiOrigin } from '../lib/api';
import { useCurrency } from '../context/CurrencyContext';
import { 
  Clock, ShieldCheck, MapPin, ChevronDown, Star, CheckCircle, 
  BadgeDollarSign, Calendar, Tag, ChevronLeft, ChevronRight,
  Play, RefreshCw, FileText, HelpCircle, Package, Layers
} from 'lucide-react';
import { countryNameFromCode } from '../lib/countries';
import { slugify, initialsOf } from '../lib/slug';
import { getPublicDisplayName, maskLastNameFromName } from '../utils/displayName';
import ContactCreativeButton from '../components/ContactCreativeButton';
import ServiceBookingModal from '../components/booking/ServiceBookingModal';
import FikaBookingModal from '../components/booking/FikaBookingModal';
import Skeleton from '../components/Skeleton';
import logger from '../lib/logger';

// Normalize image URLs
function normalizeImageUrl(src) {
  if (!src) return '';
  const s = String(src).trim();
  if (/^(data:|https?:)/i.test(s)) return s;
  if (s.startsWith('//')) return `${window?.location?.protocol || 'https:'}${s}`;
  if (s.startsWith('/')) return `${apiOrigin}${s}`;
  return `${apiOrigin}/${s}`;
}

// Parse JSONB field safely
function parseJsonField(field, defaultValue = []) {
  if (!field) return defaultValue;
  if (Array.isArray(field)) return field;
  if (typeof field === 'string') {
    try { return JSON.parse(field); } catch { return defaultValue; }
  }
  return defaultValue;
}

export default function ServiceDetail() {
  const { id, slug } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { formatPrice, convert, currency: userCurrency } = useCurrency();
  
  // Data state
  const [service, setService] = useState(null);
  const [creative, setCreative] = useState(null);
  const [packages, setPackages] = useState([]);
  const [categoryMap, setCategoryMap] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [related, setRelated] = useState([]);
  const [reviews, setReviews] = useState([]);
  
  // UI state
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [imgIndex, setImgIndex] = useState(0);
  const [faqOpen, setFaqOpen] = useState(null);
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [showFikaModal, setShowFikaModal] = useState(false);
  const [showVideo, setShowVideo] = useState(false);
  
  // Ref to prevent infinite redirect loops
  const hasRedirected = useRef(false);
  // Helper to extract YouTube video ID
  const getYouTubeId = (url) => {
    if (!url) return null;
    const match = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^\&\s]+)/);
    return match ? match[1] : null;
  };

  // Image candidates - combine featured_image, images array, etc.
  const imageCandidates = useMemo(() => {
    const imagesField = service?.images;
    const imagesCandidateList = parseJsonField(imagesField, [])
      .map((x) => typeof x === 'string' ? x : (x?.url || x?.src || x?.image_url || ''))
      .filter(Boolean);
    
    // Get video thumbnail as first image if video exists
    const ytId = getYouTubeId(service?.video_url);
    const videoThumb = ytId ? `https://img.youtube.com/vi/${ytId}/hqdefault.jpg` : null;
    const raw = [
      service?.featured_image,
      videoThumb, 
      service?.image, 
      service?.image_url,
      service?.cover_image, 
      service?.header_image,
      ...imagesCandidateList,
    ].filter(Boolean).map(normalizeImageUrl);
    
    const seen = new Set();
    const uniq = [];
    for (const u of raw) {
      if (!u || seen.has(u)) continue;
      seen.add(u);
      uniq.push(u);
    }
    return uniq;
  }, [service]);

  // Fetch service data
  useEffect(() => {
    let mounted = true;
    async function fetchData() {
      setLoading(true);
      setError(null);
      try {
        const key = slug ?? id ?? '';
        const fullUuidRegex = /([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})$/i;
        const fullMatch = String(key).match(fullUuidRegex);
        let svcRes;
        
        if (fullMatch) {
          svcRes = await apiGet(`/services/${fullMatch[1]}`);
        } else {
          const m = String(key).match(/^(?<base>[a-z0-9-]+?)(?:-(?<initials>[a-z0-9]{1,3}))?(?:-(?<token>[0-9a-f]{6,12}))?$/i);
          const base = m?.groups?.base || String(key);
          const token = m?.groups?.token || '';
          try {
            svcRes = await apiGet(`/services/slug/${encodeURIComponent(base)}`, token ? { token } : undefined);
          } catch {
            const searchRes = await apiGet('/services/public', { q: base, limit: 1 });
            const arr = searchRes?.data?.services || searchRes?.services || [];
            if (arr.length > 0) svcRes = { data: arr[0] };
          }
        }
        
        const svc = svcRes?.data || null;
        if (!svc) throw new Error('Service not found');
        if (!mounted) return;
        setService(svc);

        // Fetch creative info
        if (svc.creative_id) {
          try {
            const tRes = await apiGet(`/creatives/${svc.creative_id}`);
            if (mounted) setCreative(tRes?.data ?? null);
          } catch { /* ignore */ }
        }

        // Fetch packages from service_packages table
        try {
          const pkgRes = await apiGet(`/services/${svc.id}/packages`);
          const pkgList = pkgRes?.data?.packages || pkgRes?.packages || [];
          if (mounted) {
            setPackages(Array.isArray(pkgList) ? pkgList : []);
            if (pkgList.length > 0) setSelectedPackage(pkgList[0]);
          }
        } catch { /* ignore */ }

        // Fetch categories
        try {
          const catRes = await apiGet('/service-categories');
          const cats = catRes?.data?.data?.categories || catRes?.data?.categories || [];
          const map = Object.fromEntries(cats.map((c) => [c.id, c.name]));
          if (mounted) setCategoryMap(map);
        } catch { /* ignore */ }

        // Fetch reviews
        try {
          const revRes = await apiGet('/reviews', { service_id: svc.id, limit: 5 });
          const revList = revRes?.data?.reviews ?? revRes?.reviews ?? [];
          if (mounted) setReviews(Array.isArray(revList) ? revList : []);
        } catch { /* ignore */ }

      } catch (err) {
        if (mounted) setError(err.message || 'Failed to load service');
      } finally {
        if (mounted) setLoading(false);
      }
    }
    fetchData();
    return () => { mounted = false; };
  }, [id, slug]);

  // Fetch related services
  useEffect(() => {
    if (!service) return;
    const catName = (service?.category_id && categoryMap?.[service.category_id]) || service?.category || '';
    if (!catName) return;
    (async () => {
      try {
        const res = await apiGet('/services/public', { limit: 4, category: catName });
        const arr = res?.data?.services || res?.services || [];
        setRelated(arr.filter((s) => String(s?.id) !== String(service.id)));
      } catch { /* ignore */ }
    })();
  }, [service, categoryMap]);

  // URL canonicalization
  useEffect(() => {
    if (!service || hasRedirected.current) return;
    try {
      const titleSlug = slugify(service.title || '');
      const tNameCanonical = creative?.professional_title || creative?.name || '';
      const talentInitials = initialsOf(tNameCanonical);
      const idText = String(service.id || '').toLowerCase().replace(/[^0-9a-f-]/g, '');
      const idToken = `${idText.replace(/-/g, '').slice(0, 6)}${idText.replace(/-/g, '').slice(-6)}`;
      const canonical = titleSlug ? `${titleSlug}${talentInitials ? `-${talentInitials}` : ''}-${idToken}` : idToken;
      const currentKey = (slug ?? id ?? '').toLowerCase();
      
      if (currentKey && !currentKey.includes(idToken.toLowerCase()) && currentKey !== canonical) {
        hasRedirected.current = true;
        navigate(`/services/${canonical}`, { replace: true });
      }
    } catch { /* ignore */ }
  }, [service, creative, slug, id, navigate]);

  useEffect(() => {
    hasRedirected.current = false;
  }, [service?.id]);

  // SEO meta tags
  useEffect(() => {
    if (!service) return;
    const tUser = creative?.user || null;
    const maskedName = getPublicDisplayName(tUser) || creative?.professional_title || 'Creative';
    const title = service.meta_title || [service.title, maskedName].filter(Boolean).join(' — ');
    const desc = service.meta_description || service.short_description || service.description || '';
    document.title = title;

    const setMeta = (attr, key, val) => {
      if (!val) return;
      let tag = document.head.querySelector(`meta[${attr}="${key}"]`);
      if (!tag) {
        tag = document.createElement('meta');
        tag.setAttribute(attr, key);
        document.head.appendChild(tag);
      }
      tag.setAttribute('content', String(val));
    };

    setMeta('name', 'description', desc);
    setMeta('property', 'og:title', title);
    setMeta('property', 'og:description', desc);
    if (imageCandidates[0]) setMeta('property', 'og:image', imageCandidates[0]);
  }, [service, creative, imageCandidates]);

  // Parse JSON fields from service
  const faqItems = useMemo(() => {
    const f = parseJsonField(service?.faqs, []);
    return f.map((x) => {
      if (typeof x === 'string') return { question: x, answer: '' };
      return { question: x?.question || x?.q || '', answer: x?.answer || x?.a || '' };
    }).filter(item => item.question);
  }, [service]);

  const processSteps = useMemo(() => {
    const p = parseJsonField(service?.process_steps, []);
    return p.map((x, i) => {
      if (typeof x === 'string') return { title: x, description: '' };
      return { title: x?.title || `Step ${i + 1}`, description: x?.description || '' };
    }).filter(item => item.title);
  }, [service]);

  const benefits = useMemo(() => {
    const b = parseJsonField(service?.benefits, []);
    return b.map((x) => typeof x === 'string' ? x : (x?.title || x?.description || '')).filter(Boolean);
  }, [service]);

  const requirements = useMemo(() => {
    const r = parseJsonField(service?.requirements, []);
    return r.map((x) => typeof x === 'string' ? x : (x?.title || x?.description || '')).filter(Boolean);
  }, [service]);

  const tags = useMemo(() => {
    const t = service?.tags;
    if (Array.isArray(t)) return t.filter(Boolean);
    if (typeof t === 'string') {
      try { return JSON.parse(t); } catch { return t.split(',').map(s => s.trim()).filter(Boolean); }
    }
    return [];
  }, [service]);

  // Image slider navigation
  const nextImage = () => setImgIndex((i) => (i + 1) % imageCandidates.length);
  const prevImage = () => setImgIndex((i) => (i - 1 + imageCandidates.length) % imageCandidates.length);

  // Loading state
  if (loading) {
    return (
      <div className="space-y-4 p-4" aria-busy="true">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
          <div className="lg:col-span-3">
            <Skeleton variant="rect" className="aspect-[4/3] rounded-xl" />
          </div>
          <div className="lg:col-span-2">
            <Skeleton variant="rect" className="h-96 rounded-xl" />
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (error || !service) {
    return (
      <div className="p-4">
        <div className="rounded-xl bg-secondary-bg border border-card-border p-4">
          <div className="text-sm text-red-400">{error || 'Service not found'}</div>
          <div className="text-xs text-secondary-text mt-1">
            <Link to="/services/allservices" className="text-blue-400 hover:underline">
              Back to Services
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Computed values
  const currency = String(service.currency || 'SEK').toUpperCase();
  // Price priority: hourly_rate (shown on page) > price > package_price > base_price
  // This matches ServiceBookingModal priority to avoid confusion
  const basePrice = service.hourly_rate || service.price || selectedPackage?.price || service.package_price || service.base_price || 0;
  const deliveryDays = selectedPackage?.delivery_days || service.delivery_days || null;
  const revisions = selectedPackage?.revisions || service.revisions || 0;
  const unlimitedRevisions = service.unlimited_revisions || false;
  const categoryName = (service.category_id && categoryMap?.[service.category_id]) || service.category || '';
  const isPackage = service.is_package || false;
  const packageHours = service.package_hours || null;
  
  // Creative info
  const tUser = creative?.user || null;
  const tName = getPublicDisplayName(tUser) || creative?.professional_title || creative?.name || 'Creative';
  const proTitle = creative?.professional_title || '';
  const avatarSrc = tUser?.avatar_url || creative?.avatar || '';
  const verified = Boolean(creative?.verification_badge) || ['admin_approved', 'verified'].includes(String(creative?.verification_status || '').toLowerCase());
  const countryFull = countryNameFromCode(tUser?.country_code || creative?.country_code || '');
  const cityName = tUser?.city_name || creative?.city_name || '';
  const locationLabel = [cityName, countryFull].filter(Boolean).join(', ');
  const rating = service?.average_rating ?? creative?.rating ?? 0;
  const reviewCount = reviews.length || service?.reviews_count || 0;
  const completedOrders = service?.bookings_count || creative?.completed_bookings || 0;

  const currentImage = imageCandidates[imgIndex] || null;
  const hasMultipleImages = imageCandidates.length > 1;
  const hasVideo = Boolean(service.video_url);

  // Build creative profile link
  const creativeProfileLink = creative?.id ? `/creative/profile/${creative.id}` : '#';

  return (
    <div className="space-y-6 mb-12">
      {/* Hero Section - Side by Side Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        {/* Left: Image Slider (3/5 width on lg) */}
        <div className="lg:col-span-3">
          <div className="rounded-xl overflow-hidden bg-neutral-900 border border-card-border">
            {/* Main Image */}
            <div className="relative aspect-[4/3]">
              {currentImage ? (
                <img 
                  src={currentImage} 
                  alt={service.title} 
                  className="w-full h-full object-cover"
                  onError={() => setImgIndex((i) => Math.min(i + 1, imageCandidates.length - 1))}
                />
              ) : (
                <div className="w-full h-full bg-gradient-to-br from-zinc-800 to-zinc-900 flex items-center justify-center">
                  <Package size={64} className="text-zinc-700" />
                </div>
              )}
              
              {/* Video overlay button */}
              {hasVideo && !showVideo && imgIndex === 1 && (
                <button
                  onClick={() => setShowVideo(true)}
                  className="absolute inset-0 flex items-center justify-center bg-black/30 hover:bg-black/40 transition-colors group"
                >
                  <div className="w-16 h-16 rounded-full bg-white/90 flex items-center justify-center group-hover:scale-110 transition-transform">
                    <Play size={28} className="text-zinc-900 ml-1" />
                  </div>
                </button>
              )}
              
              {/* Navigation arrows */}
              {hasMultipleImages && (
                <>
                  <button
                    onClick={prevImage}
                    className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/50 hover:bg-black/70 flex items-center justify-center text-white transition-colors"
                    aria-label="Previous image"
                  >
                    <ChevronLeft size={24} />
                  </button>
                  <button
                    onClick={nextImage}
                    className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/50 hover:bg-black/70 flex items-center justify-center text-white transition-colors"
                    aria-label="Next image"
                  >
                    <ChevronRight size={24} />
                  </button>
                </>
              )}
              
              {/* Image counter */}
              {hasMultipleImages && (
                <div className="absolute bottom-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-black/60 text-white text-sm">
                  {imgIndex + 1} / {imageCandidates.length}
                </div>
              )}
            </div>
            
            {/* Thumbnail strip */}
            {hasMultipleImages && (
              <div className="flex gap-2 p-3 bg-card-bg overflow-x-auto">
                {imageCandidates.map((url, idx) => (
                  <button
                    key={idx}
                    onClick={() => setImgIndex(idx)}
                    className={`flex-shrink-0 w-16 h-12 rounded-lg overflow-hidden border-2 transition-all ${
                      imgIndex === idx ? 'border-blue-500 shadow-md' : 'border-transparent hover:border-blue-300 opacity-70 hover:opacity-100'
                    }`}
                  >
                    <img src={url} alt={`Thumbnail ${idx + 1}`} className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Right: Service Info Card (2/5 width on lg) */}
        <div className="lg:col-span-2">
          <div className="rounded-xl bg-secondary-bg border border-card-border p-5 h-full flex flex-col">
            {/* Category Badge */}
            {categoryName && (
              <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-medium bg-blue-500/10 text-blue-400 w-fit mb-3">
                <Tag size={12} />
                {categoryName}
              </span>
            )}
            
            {/* Service Title */}
            <h1 className="text-xl lg:text-2xl font-bold text-primary-text leading-tight">
              {service.title}
            </h1>
            
            {/* Creative Info Row */}
            <Link to={creativeProfileLink} className="flex items-center gap-3 mt-4 group">
              {avatarSrc ? (
                <img src={avatarSrc} alt={tName} className="w-10 h-10 rounded-full object-cover border border-card-border" />
              ) : (
                <div className="w-10 h-10 rounded-full bg-subtle-bg flex items-center justify-center text-sm font-semibold text-primary-text border border-card-border">
                  {tName.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase()}
                </div>
              )}
              <div className="flex-1 min-w-0">
                <div className="font-medium text-primary-text group-hover:text-blue-500 transition-colors truncate">
                  {tName}
                </div>
                {proTitle && (
                  <div className="text-xs text-secondary-text truncate">{proTitle}</div>
                )}
              </div>
              {verified && (
                <span className="flex items-center gap-1 text-xs text-emerald-500 bg-emerald-500/10 px-2 py-1 rounded-full">
                  <ShieldCheck size={12} />
                  Verified
                </span>
              )}
            </Link>
            
            {/* Stats Row */}
            <div className="flex flex-wrap items-center gap-4 mt-4 text-sm text-secondary-text">
              <span className="inline-flex items-center gap-1">
                <Star size={14} className="text-yellow-500" />
                <span className="text-primary-text font-medium">{Number(rating).toFixed(1)}</span>
                <span>({reviewCount} reviews)</span>
              </span>
              {completedOrders > 0 && (
                <span className="inline-flex items-center gap-1">
                  <CheckCircle size={14} className="text-green-500" />
                  {completedOrders} orders
                </span>
              )}
            </div>
            
            {/* Divider */}
            <div className="border-t border-card-border my-4" />
            
            {/* Price Section */}
            <div className="flex items-baseline gap-2">
              <span className="text-3xl font-bold text-primary-text">
                {basePrice > 0 ? formatPrice(convert(basePrice, currency, userCurrency), userCurrency) : 'Contact'}
              </span>
              {/* Package indicator hidden for next release */}
              {!isPackage && service.hourly_rate && <span className="text-secondary-text text-sm">/ hour</span>}
            </div>
            
            {/* Delivery Info */}
            <div className="flex flex-wrap items-center gap-4 mt-3 text-sm text-secondary-text">
              {deliveryDays && (
                <span className="inline-flex items-center gap-1.5">
                  <Clock size={14} className="text-blue-500" />
                  Delivers in {deliveryDays} days
                </span>
              )}
              <span className="inline-flex items-center gap-1.5">
                <RefreshCw size={14} className="text-blue-500" />
                {unlimitedRevisions ? 'Unlimited revisions' : `${revisions || 'Flexible'} revisions`}
              </span>
            </div>
            
            {/* Package Selector - HIDDEN FOR NEXT RELEASE
            {packages.length > 1 && (
              <div className="mt-4">
                <div className="text-xs text-secondary-text mb-2">Select Package:</div>
                <div className="flex gap-2 overflow-x-auto pb-1">
                  {packages.map((pkg) => {
                    const isSelected = selectedPackage?.id === pkg.id;
                    return (
                      <button
                        key={pkg.id}
                        onClick={() => setSelectedPackage(pkg)}
                        className={`flex-shrink-0 px-3 py-2 rounded-lg border text-sm transition-all ${
                          isSelected 
                            ? 'border-blue-500 bg-blue-500/10 text-primary-text' 
                            : 'border-card-border bg-card-bg text-secondary-text hover:border-blue-300'
                        }`}
                      >
                        <div className="font-medium">{pkg.name}</div>
                        <div className="text-xs mt-0.5">{formatPrice(pkg.price, pkg.currency || currency)} {pkg.currency || currency}</div>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
            */}
            
            {/* Spacer */}
            <div className="flex-1" />
            
            {/* Action Buttons */}
            <div className="mt-6 space-y-3">
              <button 
                type="button" 
                onClick={() => setShowBookingModal(true)} 
                className="w-full btn btn-primary py-3 text-base font-semibold"
              >
                Order Now
              </button>
              <button
                type="button"
                onClick={() => setShowFikaModal(true)}
                className="w-full btn btn-outline py-3 text-base font-semibold flex items-center justify-center gap-2"
              >
                <Calendar size={18} />
                Book a Fika
              </button>
              <ContactCreativeButton 
                creativeUserId={tUser?.id || creative?.user?.id || creative?.id} 
                label="Message" 
                icon={false} 
                className="w-full btn btn-outline py-3 text-base font-semibold block text-center" 
              />
            </div>
            
            {/* Guarantee */}
            <div className="mt-4 flex items-center gap-2 text-xs text-secondary-text">
              <ShieldCheck size={14} className="text-green-500" />
              <span>100% Money Back Guarantee</span>
            </div>
          </div>
        </div>
      </div>

      {/* Video Modal */}
      {showVideo && hasVideo && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80" onClick={() => setShowVideo(false)}>
          <div className="relative w-full max-w-4xl aspect-video" onClick={(e) => e.stopPropagation()}>
            <iframe
              src={service.video_url?.includes("youtube.com/watch") ? service.video_url.replace("watch?v=", "embed/").split("&")[0] : service.video_url}
              className="w-full h-full rounded-xl"
              allowFullScreen
              frameBorder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" referrerPolicy="strict-origin-when-cross-origin" title="Video"
            />
            <button
              onClick={() => setShowVideo(false)}
              className="absolute -top-10 right-0 text-white hover:text-gray-300"
            >
              Close
            </button>
          </div>
        </div>
      )}

      {/* Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Main Content (2/3) */}
        <div className="lg:col-span-2 space-y-4">
          {/* About This Service */}
          <div className="rounded-xl bg-secondary-bg border border-card-border p-5">
            <h2 className="text-lg font-semibold text-primary-text">About This Service</h2>
            <p className="mt-3 text-secondary-text whitespace-pre-line leading-relaxed">
              {service.description || service.short_description || 'No description available.'}
            </p>
          </div>

          {/* Service Details */}
          <div className="rounded-xl bg-secondary-bg border border-card-border p-5">
            <h2 className="text-lg font-semibold text-primary-text">Service Details</h2>
            <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 gap-4 text-sm">
              {deliveryDays && (
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center">
                    <Clock size={18} className="text-blue-500" />
                  </div>
                  <div>
                    <div className="text-secondary-text text-xs">Delivery</div>
                    <div className="text-primary-text font-medium">{deliveryDays} days</div>
                  </div>
                </div>
              )}
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center">
                  <RefreshCw size={18} className="text-blue-500" />
                </div>
                <div>
                  <div className="text-secondary-text text-xs">Revisions</div>
                  <div className="text-primary-text font-medium">
                    {unlimitedRevisions ? 'Unlimited' : (revisions || 'As needed')}
                  </div>
                </div>
              </div>
              {/* Package Hours - HIDDEN FOR NEXT RELEASE
              {isPackage && packageHours && (
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center">
                    <Calendar size={18} className="text-blue-500" />
                  </div>
                  <div>
                    <div className="text-secondary-text text-xs">Package Hours</div>
                    <div className="text-primary-text font-medium">{packageHours} hours</div>
                  </div>
                </div>
              )}
              */}
              {service.hourly_rate && !isPackage && (
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center">
                    <BadgeDollarSign size={18} className="text-blue-500" />
                  </div>
                  <div>
                    <div className="text-secondary-text text-xs">Hourly Rate</div>
                    <div className="text-primary-text font-medium">{formatPrice(convert(service.hourly_rate, currency, userCurrency), userCurrency)}</div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Why Choose This Service */}
          {benefits.length > 0 && (
            <div className="rounded-xl bg-secondary-bg border border-card-border p-5">
              <h2 className="text-lg font-semibold text-primary-text">Why Choose This Service</h2>
              <ul className="mt-4 space-y-3">
                {benefits.map((benefit, i) => (
                  <li key={i} className="flex items-start gap-3 text-secondary-text">
                    <CheckCircle size={18} className="text-green-500 flex-shrink-0 mt-0.5" />
                    <span>{benefit}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Requirements */}
          {requirements.length > 0 && (
            <div className="rounded-xl bg-secondary-bg border border-card-border p-5">
              <h2 className="text-lg font-semibold text-primary-text flex items-center gap-2">
                <FileText size={20} className="text-blue-500" />
                What You'll Need to Provide
              </h2>
              <ul className="mt-4 space-y-3">
                {requirements.map((req, i) => (
                  <li key={i} className="flex items-start gap-3 text-secondary-text">
                    <span className="w-6 h-6 rounded-full bg-blue-500/10 flex items-center justify-center flex-shrink-0 text-xs font-medium text-blue-500">
                      {i + 1}
                    </span>
                    <span>{req}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* How It Works */}
          {processSteps.length > 0 && (
            <div className="rounded-xl bg-secondary-bg border border-card-border p-5">
              <h2 className="text-lg font-semibold text-primary-text">How It Works</h2>
              <div className="mt-4 space-y-4">
                {processSteps.map((step, i) => (
                  <div key={i} className="flex gap-4">
                    <div className="w-8 h-8 rounded-full bg-blue-500 flex items-center justify-center flex-shrink-0">
                      <span className="text-sm font-semibold text-white">{i + 1}</span>
                    </div>
                    <div className="pt-1">
                      <h4 className="font-medium text-primary-text">{step.title}</h4>
                      {step.description && (
                        <p className="text-sm text-secondary-text mt-1">{step.description}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Packages Comparison - HIDDEN FOR NEXT RELEASE
          {packages.length > 0 && (
            <div className="rounded-xl bg-secondary-bg border border-card-border p-5">
              <h2 className="text-lg font-semibold text-primary-text flex items-center gap-2">
                <Layers size={20} className="text-blue-500" />
                Available Packages
              </h2>
              <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {packages.map((pkg) => {
                  const isSelected = selectedPackage?.id === pkg.id;
                  const isPopular = pkg.tier === 'standard';
                  const pkgFeatures = parseJsonField(pkg.features, []);
                  return (
                    <button
                      key={pkg.id}
                      onClick={() => setSelectedPackage(pkg)}
                      className={`relative text-left p-4 rounded-xl border-2 transition-all ${
                        isSelected 
                          ? 'border-blue-500 bg-blue-500/5' 
                          : 'border-card-border hover:border-blue-300 bg-card-bg'
                      }`}
                    >
                      {isPopular && (
                        <span className="absolute -top-2.5 left-1/2 -translate-x-1/2 px-2 py-0.5 text-xs font-medium bg-blue-500 text-white rounded-full">
                          Popular
                        </span>
                      )}
                      <div className="pt-1">
                        <h4 className="font-semibold text-primary-text text-center">{pkg.name}</h4>
                        <div className="text-xl font-bold text-primary-text text-center mt-1">
                          {formatPrice(pkg.price, pkg.currency || currency)} {pkg.currency || currency}
                        </div>
                        {pkg.short_description && (
                          <p className="text-xs text-secondary-text mt-2 text-center line-clamp-2">
                            {pkg.short_description}
                          </p>
                        )}
                      </div>
                      <div className="mt-3 pt-3 border-t border-card-border space-y-2 text-sm text-secondary-text">
                        <div className="flex justify-between">
                          <span>Delivery</span>
                          <span className="text-primary-text">{pkg.delivery_days} days</span>
                        </div>
                        <div className="flex justify-between">
                          <span>Revisions</span>
                          <span className="text-primary-text">{pkg.revisions || 0}</span>
                        </div>
                      </div>
                      {pkgFeatures.length > 0 && (
                        <ul className="mt-3 pt-3 border-t border-card-border space-y-1">
                          {pkgFeatures.slice(0, 4).map((f, i) => (
                            <li key={i} className="flex items-center gap-1 text-xs text-secondary-text">
                              <CheckCircle size={12} className="text-green-500 flex-shrink-0" />
                              <span className="truncate">{typeof f === 'string' ? f : f?.name || f?.title}</span>
                            </li>
                          ))}
                        </ul>
                      )}
                      {isSelected && (
                        <div className="absolute top-2 right-2">
                          <CheckCircle size={18} className="text-blue-500" />
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
          */}

          {/* FAQ */}
          {faqItems.length > 0 && (
            <div className="rounded-xl bg-secondary-bg border border-card-border p-5">
              <h2 className="text-lg font-semibold text-primary-text flex items-center gap-2">
                <HelpCircle size={20} className="text-blue-500" />
                Frequently Asked Questions
              </h2>
              <div className="mt-4 space-y-2">
                {faqItems.map((item, i) => (
                  <div key={i} className="border border-card-border rounded-lg overflow-hidden">
                    <button
                      onClick={() => setFaqOpen(faqOpen === i ? null : i)}
                      className="w-full flex items-center justify-between p-4 text-left hover:bg-subtle-bg transition-colors"
                    >
                      <span className="font-medium text-primary-text">{item.question}</span>
                      <ChevronDown 
                        size={18} 
                        className={`text-secondary-text transition-transform ${faqOpen === i ? 'rotate-180' : ''}`} 
                      />
                    </button>
                    {faqOpen === i && item.answer && (
                      <div className="px-4 pb-4 text-sm text-secondary-text">
                        {item.answer}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Reviews */}
          {reviews.length > 0 && (
            <div className="rounded-xl bg-secondary-bg border border-card-border p-5">
              <h2 className="text-lg font-semibold text-primary-text">Reviews</h2>
              <div className="mt-4 space-y-4">
                {reviews.map((review) => {
                  const reviewer = review.reviewer || {};
                  const name = maskLastNameFromName(reviewer.display_name || reviewer.name || '') || 'Anonymous';
                  const avatar = reviewer.avatar_url || '';
                  return (
                    <div key={review.id} className="pb-4 border-b border-card-border last:border-0 last:pb-0">
                      <div className="flex items-center gap-3 mb-2">
                        {avatar ? (
                          <img src={avatar} alt={name} className="w-10 h-10 rounded-full object-cover" />
                        ) : (
                          <div className="w-10 h-10 rounded-full bg-subtle-bg flex items-center justify-center text-sm font-semibold text-primary-text">
                            {name.charAt(0).toUpperCase()}
                          </div>
                        )}
                        <div className="flex-1">
                          <div className="font-medium text-primary-text">{name}</div>
                          <div className="flex items-center gap-2 text-xs text-secondary-text">
                            <div className="flex items-center gap-1">
                              <Star size={12} className="text-yellow-500" />
                              {Number(review.rating).toFixed(1)}
                            </div>
                            <span>•</span>
                            <span>{new Date(review.created_at).toLocaleDateString()}</span>
                          </div>
                        </div>
                      </div>
                      {review.comment && (
                        <p className="text-sm text-secondary-text">{review.comment}</p>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* Right Column - Sidebar (1/3) */}
        <div className="space-y-4">
          {/* About the Creative */}
          <div className="rounded-xl bg-secondary-bg border border-card-border p-5">
            <h3 className="text-base font-semibold text-primary-text">About the Creative</h3>
            <div className="mt-4 flex flex-col items-center text-center">
              {avatarSrc ? (
                <img src={avatarSrc} alt={tName} className="w-20 h-20 rounded-full object-cover border-2 border-card-border" />
              ) : (
                <div className="w-20 h-20 rounded-full bg-subtle-bg flex items-center justify-center text-xl font-semibold text-primary-text border-2 border-card-border">
                  {tName.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase()}
                </div>
              )}
              <div className="mt-3 font-semibold text-primary-text">{tName}</div>
              {proTitle && <div className="text-sm text-secondary-text mt-1">{proTitle}</div>}
              <div className="flex items-center gap-1 mt-2">
                <Star size={14} className="text-yellow-500" />
                <span className="text-sm text-primary-text font-medium">{Number(creative?.rating || 0).toFixed(1)}</span>
                <span className="text-sm text-secondary-text">({creative?.total_reviews || 0})</span>
              </div>
              {locationLabel && (
                <div className="flex items-center gap-1 mt-2 text-sm text-secondary-text">
                  <MapPin size={14} />
                  <span>{locationLabel}</span>
                </div>
              )}
            </div>
            <Link
              to={creativeProfileLink}
              className="block w-full mt-4 py-2.5 text-center rounded-lg border border-card-border text-primary-text hover:bg-subtle-bg transition-colors text-sm font-medium"
            >
              View Full Profile
            </Link>
          </div>

          {/* Performance */}
          <div className="rounded-xl bg-secondary-bg border border-card-border p-5">
            <h3 className="text-base font-semibold text-primary-text">Performance</h3>
            <div className="mt-4 space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-secondary-text">Rating</span>
                <span className="flex items-center gap-1 text-primary-text font-medium">
                  <Star size={14} className="text-yellow-500" />
                  {Number(rating).toFixed(1)}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-secondary-text">Reviews</span>
                <span className="text-primary-text font-medium">{reviewCount}</span>
              </div>
              {completedOrders > 0 && (
                <div className="flex items-center justify-between">
                  <span className="text-secondary-text">Orders Completed</span>
                  <span className="text-primary-text font-medium">{completedOrders}</span>
                </div>
              )}
              {creative?.response_time_hours && (
                <div className="flex items-center justify-between">
                  <span className="text-secondary-text">Response Time</span>
                  <span className="text-primary-text font-medium">{creative.response_time_hours}h</span>
                </div>
              )}
            </div>
          </div>

          {/* Tags */}
          {tags.length > 0 && (
            <div className="rounded-xl bg-secondary-bg border border-card-border p-5">
              <h3 className="text-base font-semibold text-primary-text">Tags</h3>
              <div className="mt-3 flex flex-wrap gap-2">
                {tags.map((tag, i) => (
                  <span 
                    key={i} 
                    className="inline-flex items-center text-xs px-2.5 py-1.5 rounded-lg bg-card-bg text-secondary-text border border-card-border hover:border-blue-400 hover:text-blue-400 transition-colors cursor-pointer"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Guarantee Badge */}
          <div className="rounded-xl bg-gradient-to-br from-green-500/10 to-emerald-500/5 border border-green-500/20 p-5">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-green-500/20 flex items-center justify-center">
                <ShieldCheck size={24} className="text-green-500" />
              </div>
              <div>
                <div className="font-medium text-primary-text">Money Back Guarantee</div>
                <div className="text-xs text-secondary-text mt-0.5">100% satisfaction or your money back</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Related Services */}
      {related.length > 0 && (
        <div className="rounded-xl bg-secondary-bg border border-card-border p-5 lg:p-6">
          <h2 className="text-lg font-semibold text-primary-text">Related Services</h2>
          <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {related.slice(0, 4).map((s) => {
              const imgArr = Array.isArray(s.images) && s.images.length > 0 ? (typeof s.images[0] === 'string' ? s.images[0] : s.images[0]?.url) : null; const img = normalizeImageUrl(s.featured_image || imgArr || s.image || s.image_url || '');
              const titleSlug = slugify(s.title || '');
              const idText = String(s.id || '').toLowerCase().replace(/[^0-9a-f-]/g, '');
              const idToken = `${idText.replace(/-/g, '').slice(0, 6)}${idText.replace(/-/g, '').slice(-6)}`;
              const toPath = titleSlug ? `/services/${titleSlug}-${idToken}` : `/services/${idToken}`;
              const relatedCurrency = String(s.currency || 'SEK').toUpperCase();
              const relatedPrice = s.hourly_rate || s.price || s.base_price || 0;
              const convertedRelatedPrice = convert(relatedPrice, relatedCurrency, userCurrency);
              
              return (
                <Link 
                  key={s.id} 
                  to={toPath}
                  className="bg-card-bg rounded-xl border border-card-border overflow-hidden hover:shadow-lg hover:-translate-y-1 transition-all"
                >
                  <div className="aspect-video bg-subtle-bg relative">
                    {img ? (
                      <img src={img} alt={s.title} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full bg-gradient-to-br from-zinc-800 to-zinc-900 flex items-center justify-center">
                        <Package size={32} className="text-zinc-700" />
                      </div>
                    )}
                  </div>
                  <div className="p-3">
                    <h3 className="font-medium text-primary-text text-sm line-clamp-2">{s.title}</h3>
                    <div className="flex items-center justify-between mt-2 text-xs">
                      <span className="inline-flex items-center gap-1 text-secondary-text">
                        <Star size={12} className="text-yellow-500" />
                        {Number(s.average_rating || 0).toFixed(1)}
                      </span>
                      <span className="text-primary-text font-medium">
                        {formatPrice(convertedRelatedPrice, userCurrency)}
                      </span>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      )}

      {/* Booking Modal */}
      <ServiceBookingModal
        isOpen={showBookingModal}
        onClose={() => setShowBookingModal(false)}
        service={service}
        creative={creative}
        packages={packages}
        selectedPackage={selectedPackage}
      />

      {/* Fika Booking Modal */}
      <FikaBookingModal
        isOpen={showFikaModal}
        onClose={() => setShowFikaModal(false)}
        creative={creative}
        service={service}
      />
    </div>
  );
}
